import React, { useState, useEffect } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
} from "chart.js";
import { Bar, Line } from "react-chartjs-2";
import {
  MobileReportsContainer,
  PeriodToggleContainer,
  PeriodToggleButton,
  ChartSection,
  ChartLegendContainer,
  LegendItemNew,
  LegendDot,
  LegendText,
  MobileChartContainer,
  TotalOrdersTitle,
  TopSellingSection,
  TopSellingList,
  TopSellingItem,
  ItemName,
  NoItemsText,
  ItemImage,
  ItemPrice,
  NoChartDataContainer,
} from "../../styles/Reports.styled";
import {
  FigmaBalanceDivider,
  FigmaBalanceItem,
  FigmaBalanceItemTitle,
  FigmaBalanceItemValueReport,
  FigmaPendingBalanceRow,
} from "../../styles/Wallet.styled";
import {
  ReportOrderStatus,
} from "../../styles/OrderHistory.styled";
import { FlexContainer } from "../../styles/PersonalInfoStep";
import { useErrorResponse } from "../../services/useErrorResponse";
import { POST } from "../../services/HTTPServices";
import placeholder from "../../assets/svgs/placeholder.svg";
import { LoadingOverlay,LoadingSpinner,LoadingSpinnerIcon } from "../../styles/loading";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

// eslint-disable-next-line @typescript-eslint/no-redeclare
interface TopSellingItem {
  productId: string;
  productName: string;
  productImage?: string;
  qty: number;
  price?: number;
  status?: string;
}

interface SalesOverviewData {
  grossSales: number;
  netSales: number;
  totalOrders: number;
  totalSales?: number;
  topSellingItems: TopSellingItem[];
}

interface ChartDataPoint {
  date: string;
  grossSales: number;
  netSales: number;
  fullDate: Date;
}

interface SalesChartDataPoint {
  label: string | number;
  grossSales: number;
  netSales: number;
}

const Reports: React.FC = () => {
  const [selectedPeriod, setSelectedPeriod] = useState<
    "today" | "7days" | "30days"
  >("today");
  const [salesData, setSalesData] = useState<SalesOverviewData>({
    grossSales: 0,
    netSales: 0,
    totalOrders: 0,
    topSellingItems: [],
  });
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [auth, setAuth] = useState<any>(null);

  const outErrorResponse = useErrorResponse();
  const displayStatus = (status: any) => {
    switch (status) {
      case "d":
        return "Deleted";
      case "a":
        return "Available";
      case "u":
        return "Unavailable";
      default:
        return "Available";
    }
  };
  const getSaleOverview = async (period: "today" | "7days" | "30days") => {
    if (!auth?.id) return;

    let url = "report/SalesOverview";

    let param = {
      serviceProviderId: auth.id.toString(),
      reportPeriod: period === "today" ? 1 : period === "7days" ? 2 : 3,
    };

    let res: any = await POST(url, param, "payment");
    if (res && res.status === 200) {
      const data: SalesOverviewData = res.data;


      const filledData: SalesOverviewData = {
        ...data,
        topSellingItems: data.topSellingItems || [],
        totalSales: data.totalSales ?? (data.netSales || data.grossSales),
      };

      setSalesData(filledData);
    } else {
      outErrorResponse(res.status, res.message);
    }
  };

  const getSaleChartOverview = async (period: "today" | "7days" | "30days") => {
    if (!auth?.id) return;

    setLoading(true);
    let url = "report/SalesChartOverview";

    let param = {
      serviceProviderId: auth.id.toString(),
      reportPeriod: period === "today" ? 1 : period === "7days" ? 2 : 3,
    };

    let res: any = await POST(url, param, "payment");
    if (res && res.status === 200) {
      const data: SalesChartDataPoint[] = res.data.salesChartOverview;

      // Convert API data to chart format
      const convertedChartData = convertApiDataToChartData(data, period);
      setChartData(convertedChartData);

      setLoading(false);
    } else {
      setLoading(false);
      outErrorResponse(res.status, res.message);
    }
  };

  // Convert API response to chart data format
  const convertApiDataToChartData = (
    apiData: SalesChartDataPoint[],
    period: "today" | "7days" | "30days"
  ): ChartDataPoint[] => {
    const today = new Date();

    return apiData.map((item, index) => {
      let dateLabel: string;
      let fullDate: Date;

      if (period === "today") {
        dateLabel = "Today";
        fullDate = new Date(today);
      } else if (period === "7days") {
        if (typeof item.label === "string") {
          dateLabel = item.label;
        } else {
          const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
          dateLabel = dayNames[item.label] || `Day ${item.label}`;
        }
        const daysAgo = 6 - index;
        fullDate = new Date(today);
        fullDate.setDate(fullDate.getDate() - daysAgo);
      } else {
        if (typeof item.label === "number") {
          dateLabel = item.label.toString();
        } else {
          dateLabel = item.label;
        }
        const daysAgo = (apiData.length - 1 - index) * 5; // Every 5 days for 30-day period
        fullDate = new Date(today);
        fullDate.setDate(fullDate.getDate() - daysAgo);
      }

      return {
        date: dateLabel,
        grossSales: item.grossSales,
        netSales: item.netSales,
        fullDate: fullDate,
      };
    });
  };

  useEffect(() => {
    localStorage.removeItem("selected_voucher");
    const authUser = JSON.parse(localStorage.getItem("authUser") || "null");
    if (authUser) {
      setAuth(authUser);
    }
  }, []);

  useEffect(() => {
    if (auth?.id) {
      getSaleOverview(selectedPeriod);
      getSaleChartOverview(selectedPeriod);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth, selectedPeriod]);

  const handlePeriodChange = (period: "today" | "7days" | "30days") => {
    setSelectedPeriod(period);
  };

  // Chart.js configuration
  const barChartData = {
    labels: ["Gross Sales", "Net Sales"],
    datasets: [
      {
        label: "Sales",
        data: [
          chartData.length > 0 ? chartData[0].grossSales : 0,
          chartData.length > 0 ? chartData[0].netSales : 0,
        ],
        backgroundColor: ["#e39435", "#76B041"],
        borderWidth: 0,
        barThickness: 60,
      },
    ],
  };

  const lineChartData = {
    labels: chartData.map((d) => d.date),
    datasets: [
      {
        label: "Gross Sales",
        data: chartData.map((d) => d.grossSales),
        borderColor: "#e39435",
        backgroundColor: "rgba(227, 148, 53, 0.1)",
        borderWidth: 2,
        pointBackgroundColor: "#e39435",
        pointBorderColor: "#e39435",
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        tension: 0.1,
      },
      {
        label: "Net Sales",
        data: chartData.map((d) => d.netSales),
        borderColor: "#76B041",
        backgroundColor: "rgba(16, 185, 129, 0.1)",
        borderWidth: 2,
        pointBackgroundColor: "#76B041",
        pointBorderColor: "#76B041",
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        tension: 0.1,
      },
    ],
  };

  const lineChartOptions: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: "index" as const,
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "white",
        titleColor: "#1e293b",
        bodyColor: "#64748b",
        borderColor: "#e2e8f0",
        borderWidth: 1,
        callbacks: {
          title: function (context) {
            return `Day: ${context[0]?.label ?? "-"}`;
          },
          label: function (context) {
            const y = typeof context.parsed?.y === "number" ? context.parsed.y : 0;
            return `${context.dataset.label}: $${y.toFixed(2)}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          color: "#f1f5f9",
        },
        ticks: {
          color: "#64748b",
          font: {
            size: 12,
          },
          maxTicksLimit: selectedPeriod === "30days" ? 6 : undefined,
        },
      },
      y: {
        grid: {
          color: "#f1f5f9",
        },
        ticks: {
          color: "#64748b",
          font: {
            size: 12,
          },
          callback: function (value) {
            return `$${Number(value).toFixed(2)}`;
          },
        },
        beginAtZero: true,
        suggestedMin: 0,
        grace: "5%",
      },
    },
  };

  const barChartOptions: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: "index" as const,
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "white",
        titleColor: "#1e293b",
        bodyColor: "#64748b",
        borderColor: "#e2e8f0",
        borderWidth: 1,
        callbacks: {
          label: function (context) {
            const y = typeof context.parsed?.y === "number" ? context.parsed.y : 0;
            return `${context.dataset.label}: $${y.toFixed(2)}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: "#64748b",
          font: {
            size: 12,
          },
        },
      },
      y: {
        grid: {
          color: "#d1d5db",
        },
        ticks: {
          color: "#64748b",
          font: {
            size: 12,
          },
          callback: function (value) {
            return `$${Number(value).toFixed(2)}`;
          },
          stepSize: 0.25,
        },
        beginAtZero: true,
        max: 1.0,
      },
    },
    backgroundColor: "#f9fafb",
  };

  return (
    <>
    {loading && (
            <LoadingOverlay>
              <LoadingSpinner>
                <LoadingSpinnerIcon />
              </LoadingSpinner>
            </LoadingOverlay>
          )}

          
    <MobileReportsContainer>
      {/* Period Toggle Buttons */}
      <PeriodToggleContainer>
        <PeriodToggleButton
          $active={selectedPeriod === "today"}
          onClick={() => handlePeriodChange("today")}
        >
          Today
        </PeriodToggleButton>
        <PeriodToggleButton
          $active={selectedPeriod === "7days"}
          onClick={() => handlePeriodChange("7days")}
        >
          Past 7 Days
        </PeriodToggleButton>
        <PeriodToggleButton
          $active={selectedPeriod === "30days"}
          onClick={() => handlePeriodChange("30days")}
        >
          Past 30 Days
        </PeriodToggleButton>
      </PeriodToggleContainer>

      {/* Chart Section */}
      <ChartSection>
        <ChartLegendContainer>
          <LegendItemNew>
            <LegendDot $color="#e39435" />
            <LegendText>Gross Sales</LegendText>
          </LegendItemNew>
          <LegendItemNew>
            <LegendDot $color="#76B041" />
            <LegendText>Net Sales</LegendText>
          </LegendItemNew>
        </ChartLegendContainer>

        <MobileChartContainer>
          {(() => {
            if (selectedPeriod === "today") {
              return <Bar data={barChartData} options={barChartOptions} />;
            } else {
              // Validate data before rendering
              const hasValidData =
                chartData.length > 0 &&
                chartData.some(
                  (d) => !isNaN(d.grossSales) || !isNaN(d.netSales)
                );

              if (!hasValidData) {
                return (
                  <NoChartDataContainer>
                    No data available for the selected period
                  </NoChartDataContainer>
                );
              }

              return <Line data={lineChartData} options={lineChartOptions} />;
            }
          })()}
        </MobileChartContainer>
      </ChartSection>

      {/* Total Orders Section */}

      <FigmaPendingBalanceRow>
        <FigmaBalanceItem>
          <FigmaBalanceItemTitle>Total Orders</FigmaBalanceItemTitle>
          <FigmaBalanceDivider />
          <FigmaBalanceItemValueReport>
            {salesData.totalOrders?.toFixed(2) || "-"}
          </FigmaBalanceItemValueReport>
        </FigmaBalanceItem>
        <FigmaBalanceItem>
          <FigmaBalanceItemTitle>Total Sales</FigmaBalanceItemTitle>
          <FigmaBalanceDivider />
          <FigmaBalanceItemValueReport>
            {salesData.totalSales?.toFixed(2) || "-"}
          </FigmaBalanceItemValueReport>
        </FigmaBalanceItem>
      </FigmaPendingBalanceRow>

      {/* Top-selling Items Section */}
      <TopSellingSection>
        <TotalOrdersTitle>Top Selling Items</TotalOrdersTitle>
        <FigmaBalanceDivider className="mb-16"/>

        {salesData.topSellingItems.length > 0 ? (
          <TopSellingList>
            {salesData.topSellingItems.map((item, index) => (
              <TopSellingItem key={item.productId || index}>
                <FlexContainer>
                  <ItemImage
                    src={(item.productImage && item.productImage !== "") ? item.productImage : placeholder}
                    alt={item.productName}
                  />
                  <div>
                    <ItemName>{item.productName}</ItemName>
                    <ItemPrice>${item.price} &nbsp; {item.qty} Sold</ItemPrice>
                  </div>
                </FlexContainer>

                <ReportOrderStatus $status={item.status}>
                  {displayStatus(item.status)}
                </ReportOrderStatus>
              </TopSellingItem>
            ))}
          </TopSellingList>
        ) : (
          <NoItemsText>No Data found.</NoItemsText>
        )}
      </TopSellingSection>
    </MobileReportsContainer>
    </>
  );
};

export default Reports;
